import React, { useState, useCallback, ReactNode, useEffect } from 'react';
import {
  GovernanceTagsProcessingContext,
  GovernanceTagsProcessingContextType,
} from './governanceTagsProcessingContext';
import { FIVE_MINUTES } from '../hooks/useGovernanceTags';

const PROCESSING_TIMEOUT_MS = FIVE_MINUTES;

interface GovernanceTagsProcessingProviderProps {
  children: ReactNode;
}

export const GovernanceTagsProcessingProvider: React.FC<GovernanceTagsProcessingProviderProps> = ({ children }) => {
  const [processingResources, setProcessingResources] = useState<Map<string, number>>(new Map());

  const addProcessingResource = useCallback((resourceId: string) => {
    setProcessingResources((prev) => {
      const newMap = new Map(prev);
      newMap.set(resourceId, Date.now());
      return newMap;
    });
  }, []);

  const removeProcessingResource = useCallback((resourceId: string) => {
    setProcessingResources((prev) => {
      const newMap = new Map(prev);
      newMap.delete(resourceId);
      return newMap;
    });
  }, []);

  const isResourceProcessing = useCallback(
    (resourceId: string) => {
      return processingResources.has(resourceId);
    },
    [processingResources],
  );

  const clearAllProcessing = useCallback(() => {
    setProcessingResources(new Map());
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setProcessingResources((prev) => {
        const newMap = new Map(prev);
        let hasChanges = false;

        prev.forEach((startTime, resourceId) => {
          if (now - startTime >= PROCESSING_TIMEOUT_MS) {
            newMap.delete(resourceId);
            hasChanges = true;
          }
        });

        return hasChanges ? newMap : prev;
      });
    }, 30000);

    const handleBeforeUnload = () => {
      clearAllProcessing();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [clearAllProcessing]);

  const contextValue: GovernanceTagsProcessingContextType = {
    processingResources: new Set(processingResources.keys()),
    addProcessingResource,
    removeProcessingResource,
    isResourceProcessing,
    clearAllProcessing,
  };

  return (
    <GovernanceTagsProcessingContext.Provider value={contextValue}>{children}</GovernanceTagsProcessingContext.Provider>
  );
};
